import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import AppointmentDetails from "@components/Appointments/AppointmentDetails"
import useBooking from "@hooks/useBooking"
import NavigationsPage from "@containers/NavigationsPage/NavigationPage"
import PageWrapper from "@components/Common/PageWrapper"
import { useSelector } from "react-redux"
import ArrowLeftIcon from "@assets/icons/arrowLeft"
import ArrowSquareLeftIcon from "@assets/icons/arrowSquareLeft"
import AppointmentSectionHeader from "@components/ScheduleAppointmentPage/AppointmentSectionHeader"
import PatientInfoIcon from "@assets/icons/patientInfo"
import MeetingInfoIcon from "@assets/icons/meetingInfo"
import NotesIcon from "@assets/icons/notes"

const AppointmentDetailsPage = () => {
  const [appointmentDetails, setAppointmentDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { handleCancelCalendarBooking, handleCancelGoogleEvent } = useBooking()

  // Extract appointment from location state or use ID from URL parameters
  const appointment = location.state?.appointment
  const queryParams = new URLSearchParams(location.search)
  const appointmentId = queryParams.get("id")

  useEffect(() => {
    // If we have the appointment object in location state, use it directly
    if (appointment) {
      setAppointmentDetails(appointment)
    } else {
      // No appointment data, navigate back
      navigate("/manage-appointment")
    }
  }, [appointment, navigate])

  const handleCancelAppointmentClick = async () => {
    if (!appointmentDetails || appointmentDetails.status === "cancelled") {
      return
    }

    // Start loading state
    setIsCancelLoading(true)

    const {
      patientId,
      eventId,
      location: appointmentLocation,
    } = appointmentDetails

    try {
      if (appointmentLocation === "offline") {
        // For offline appointments, just cancel the calendar booking
        await handleCancelCalendarBooking({ patientId, eventId })
      } else {
        // For online appointments, cancel Google event first, then calendar booking
        await handleCancelGoogleEvent({ eventId })
        await handleCancelCalendarBooking({ eventId, patientId })
      }

      // Update local state to show cancelled status
      setAppointmentDetails((prev) => ({
        ...prev,
        status: "cancelled",
      }))

      // Navigate back after a short delay
      setTimeout(() => {
        navigate("/manage-appointment", { state: { refreshData: true } })
      }, 1000)
    } catch (error) {
      console.error("Failed to cancel appointment:", error)
    } finally {
      setIsCancelLoading(false)
    }
  }

  const handleEditClick = () => {
    navigate(
      `/manage-appointment/schedule-appointment?edit=${appointmentId || appointment?._id}`
    )
  }

  const handleBackClick = () => {
    navigate("/manage-appointment")
  }

  return (
    <NavigationsPage>
      <PageWrapper className="w-full overflow-auto flex flex-col">
        <div className="w-full overflow-hidden flex flex-col">
          <div className="hidden md:flex gap-2 mb-8">
            <ArrowLeftIcon
              onClick={handleBackClick}
              className="cursor-pointer"
            />
            <span
              className="text-sm text-[--primary-blue] font-bold cursor-pointer"
              onClick={handleBackClick}
            >
              BACK
            </span>
          </div>
          <button className="md:hidden mb-5" onClick={handleBackClick}>
            <ArrowSquareLeftIcon />
          </button>
          <div className="w-full flex justify-between items-center mb-8">
            <div className="text-sm md:text-3xl font-bold text-[--primary-blue] md:text-[--quaternary-black] md:font-normal">
              Manage Appointment
            </div>
          </div>

          <div className="w-full flex justify-between mb-8">
            <AppointmentSectionHeader
              icon={<MeetingInfoIcon />}
              title="Appointment Details"
              appointmentStage={1}
              className="w-full border-[--primary-blue]"
            />
          </div>

          {isLoading ? (
            <div className="w-full flex justify-center items-center min-h-[400px]">
              <div className="animate-pulse">
                Loading appointment details...
              </div>
            </div>
          ) : appointmentDetails ? (
            <AppointmentDetails
              appointmentDetails={appointmentDetails}
              onEdit={handleEditClick}
              onCancel={handleCancelAppointmentClick}
              isCancelLoading={isCancelLoading}
              showEditButton={true}
              isViewOnly={true}
              className="w-full"
            />
          ) : (
            <div className="w-full text-center">
              <p>Appointment not found.</p>
              <button
                onClick={handleBackClick}
                className="mt-4 px-4 py-2 bg-[--primary-blue] text-white rounded-lg"
              >
                Back to Appointments
              </button>
            </div>
          )}
        </div>
      </PageWrapper>
    </NavigationsPage>
  )
}

export default AppointmentDetailsPage
